/**
 * SignInScreen — presentational, backend-DECOUPLED.
 *
 * Renders the sign-in card from props only. It owns LOCAL UI state (the email /
 * password fields, the inline error, the in-flight flag) but NEVER fetches or
 * navigates: authentication goes out through the async `onSignIn` callback and
 * cross-page links arrive as plain hrefs. The callback returns a discriminated
 * result so every user-facing copy string stays inside this screen, and stories
 * can drive Error / Submitting with backend-free fixtures.
 *
 * The route container keeps the post-success full-page reload to itself.
 * TOTP belongs on Security enrolment, not on this card.
 */
import * as React from "react";
import { AlertCircle, Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";
import { PublicShell } from "~/components/layout/public-shell";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { FormField } from "~/components/ui/form-field";
import { Label } from "~/components/ui/label";
import { FOCUS_RING, cn } from "~/lib/utils";

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

export interface SignInCredentials {
  email: string;
  password: string;
}

/**
 * Outcome of an authentication attempt. The container classifies the HTTP
 * response; the screen maps the reason to inline copy.
 */
export type SignInResult =
  | { ok: true }
  | { ok: false; reason: "invalid-credentials" | "error" };

export interface SignInScreenProps {
  /**
   * Authenticate with the given credentials. Resolve `{ ok: true }` on success
   * (the container performs the redirect — this screen stays in its submitting
   * state). Resolve `{ ok: false, reason }` to surface inline copy. A thrown /
   * rejected promise is treated as a generic error.
   */
  onSignIn: (credentials: SignInCredentials) => Promise<SignInResult>;
  /**
   * Destination for the "Forgot password?" link. Omitted by default: the link
   * is only rendered when there is somewhere for it to go. A dead link on the
   * sign-in page is worse than no link, because the person clicking it is
   * already locked out.
   */
  forgotPasswordHref?: string;
  /** Destination for the "Sign up" link. Omitted by default, for the same reason. */
  signupHref?: string;
}

const EMPTY_BOTH = "Enter your email and password to continue.";
const INVALID = "Email or password is incorrect.";
const GENERIC = "Something went wrong. Please try again.";

const LINK_CLASS = cn(
  "rounded-sm text-sm text-primary underline-offset-4 transition-colors duration-fast hover:underline motion-reduce:transition-none",
  FOCUS_RING,
);

function emptySubmitMessage(email: string, password: string): string | undefined {
  const emailMissing = email.trim() === "";
  const passwordMissing = password === "";
  if (emailMissing && passwordMissing) return EMPTY_BOTH;
  if (emailMissing) return "Enter your email to continue.";
  if (passwordMissing) return "Enter your password to continue.";
  return undefined;
}

function failureCopy(reason: "invalid-credentials" | "error"): string {
  return reason === "invalid-credentials" ? INVALID : GENERIC;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function SignInScreen({
  onSignIn,
  forgotPasswordHref,
  signupHref,
}: SignInScreenProps): React.ReactElement {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [apiError, setApiError] = React.useState<string | undefined>(undefined);
  const [emailInvalid, setEmailInvalid] = React.useState(false);
  const [passwordInvalid, setPasswordInvalid] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [capsLockOn, setCapsLockOn] = React.useState(false);
  const submittingRef = React.useRef(false);
  const emailRef = React.useRef<HTMLInputElement>(null);
  const passwordRef = React.useRef<HTMLInputElement>(null);

  const clearIssue = () => {
    setApiError(undefined);
    setEmailInvalid(false);
    setPasswordInvalid(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submittingRef.current || isSubmitting) return;

    const trimmedEmail = email.trim();
    const missing = emptySubmitMessage(email, password);
    if (missing) {
      setApiError(missing);
      setEmailInvalid(trimmedEmail === "");
      setPasswordInvalid(password === "");
      if (trimmedEmail === "") emailRef.current?.focus();
      else passwordRef.current?.focus();
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    setApiError(undefined);
    setEmailInvalid(false);
    setPasswordInvalid(false);

    try {
      const result = await onSignIn({ email: trimmedEmail, password });
      if (result.ok) {
        // Success: the container redirects (full-page reload so the root auth
        // guard re-probes the session). Stay in the submitting state until
        // unload — do not clear isSubmitting, or a second submit can fire.
        return;
      }
      setApiError(failureCopy(result.reason));
      setEmailInvalid(result.reason === "invalid-credentials");
      setPasswordInvalid(result.reason === "invalid-credentials");
      passwordRef.current?.focus();
    } catch {
      setApiError(GENERIC);
    }
    submittingRef.current = false;
    setIsSubmitting(false);
  };

  const passwordDescribedBy = capsLockOn ? "signin-password-caps" : undefined;

  return (
    <PublicShell>
      <Card className="overflow-hidden">
        <CardHeader className="gap-1.5">
          <h1
            data-slot="card-title"
            className="text-2xl font-semibold leading-tight tracking-tight text-foreground"
          >
            Sign in
          </h1>
          <p className="text-sm text-foreground-muted">
            Continue with your email and password.
          </p>
        </CardHeader>

        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          aria-busy={isSubmitting || undefined}
          noValidate
        >
          <CardContent className="flex flex-col gap-5">
            {apiError ? (
              <div
                role="alert"
                className={cn(
                  "flex gap-2.5 rounded-sm border border-error/30 bg-error-soft px-3 py-2.5 text-sm text-foreground",
                  "animate-in fade-in-0 slide-in-from-top-1 duration-200 motion-reduce:animate-none",
                )}
              >
                <AlertCircle
                  className="mt-0.5 size-4 shrink-0 text-error-ink"
                  aria-hidden="true"
                />
                <p className="min-w-0 text-pretty">{apiError}</p>
              </div>
            ) : null}

            <div className="flex flex-col gap-3">
              <FormField
                id="signin-email"
                label="Email"
                required
                error={emailInvalid ? "" : undefined}
              >
                <Input
                  ref={emailRef}
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  inputMode="email"
                  enterKeyHint="next"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoFocus
                  value={email}
                  disabled={isSubmitting}
                  prefix={<Mail className="size-4" aria-hidden="true" />}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearIssue();
                  }}
                />
              </FormField>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between gap-3">
                  <Label htmlFor="signin-password" required>
                    Password
                  </Label>
                  {forgotPasswordHref ? (
                    <a href={forgotPasswordHref} className={LINK_CLASS}>
                      Forgot password?
                    </a>
                  ) : null}
                </div>
                <Input
                  ref={passwordRef}
                  id="signin-password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Your password"
                  autoComplete="current-password"
                  enterKeyHint="go"
                  spellCheck={false}
                  value={password}
                  disabled={isSubmitting}
                  aria-invalid={passwordInvalid || undefined}
                  aria-describedby={passwordDescribedBy}
                  prefix={<Lock className="size-4" aria-hidden="true" />}
                  suffix={
                    <button
                      type="button"
                      disabled={isSubmitting}
                      aria-label={showPassword ? "Hide characters" : "Show characters"}
                      aria-pressed={showPassword}
                      className={cn(
                        "inline-flex size-8 items-center justify-center rounded-sm text-foreground-muted",
                        "hover:text-foreground disabled:pointer-events-none",
                        FOCUS_RING,
                      )}
                      onClick={() => setShowPassword((visible) => !visible)}
                    >
                      {showPassword ? (
                        <EyeOff className="size-4" aria-hidden="true" />
                      ) : (
                        <Eye className="size-4" aria-hidden="true" />
                      )}
                    </button>
                  }
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clearIssue();
                  }}
                  onKeyUp={(e) => setCapsLockOn(e.getModifierState("CapsLock"))}
                  onKeyDown={(e) => setCapsLockOn(e.getModifierState("CapsLock"))}
                />
                {capsLockOn ? (
                  <p
                    id="signin-password-caps"
                    role="status"
                    className="text-xs text-foreground-muted"
                  >
                    Caps Lock is on
                  </p>
                ) : null}
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Loader2
                    className="size-4 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </CardContent>
        </form>

        <CardFooter className="justify-center border-t border-border pt-6">
          {signupHref ? (
            <p className="text-pretty text-sm text-foreground-muted">
              Don&apos;t have an account?{" "}
              <a href={signupHref} className={cn(LINK_CLASS, "text-foreground hover:text-primary")}>
                Sign up
              </a>
            </p>
          ) : (
            <p className="text-pretty text-sm text-foreground-muted">
              InboxFury workspaces are created by invitation. Ask whoever
              administers yours, or write to{" "}
              <a href="mailto:support@inboxfury.com" className={LINK_CLASS}>
                support@inboxfury.com
              </a>
              .
            </p>
          )}
        </CardFooter>
      </Card>
    </PublicShell>
  );
}
